import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { TAG_REQUESTS_BASE, tagRequestKeys, unwrap } from './tag-requests.query';
import type { TagDecision, TagRequest } from '@/client/types/tag-requests';

type DecideVars = { etrid: number; decisions: TagDecision[]; reviewerNotes?: string };

export interface DecideResult extends TagRequest {
  approved_tagids?: number[];
  rejected_tagids?: number[];
  /** Set when the decision saved but attaching an approved tag to the listing failed. */
  apply_error?: { error?: string; error_description?: string } | null;
}

const useInvalidate = () => {
  const qc = useQueryClient();
  return (etrid: number) => {
    qc.invalidateQueries({ queryKey: tagRequestKeys.lists() });
    qc.invalidateQueries({ queryKey: tagRequestKeys.detail(etrid) });
  };
};

// JSON bodies: `admin/tag-requests/*` is on the proxy's JSON allowlist (src/server/lib/utils.ts).
export const useClaimTagRequestMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useInvalidate();
  return useMutation<unknown, Error, number>({
    mutationFn: withErrorHandling(
      async (etrid: number) => request<unknown>(`${TAG_REQUESTS_BASE}/${etrid}/claim`, 'POST', {}),
      'Could not take this request for review',
    ),
    onSuccess: (_data, etrid) => invalidate(etrid),
  });
};

/** Tags left out of `decisions` stay pending, so a reviewer can decide what they're sure of. */
export const useDecideTagRequestMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useInvalidate();
  return useMutation<DecideResult, Error, DecideVars>({
    mutationFn: withErrorHandling(
      async ({ etrid, decisions, reviewerNotes }: DecideVars) =>
        unwrap(
          await request<unknown>(`${TAG_REQUESTS_BASE}/${etrid}/decide`, 'POST', {
            decisions,
            reviewer_notes: reviewerNotes?.trim() || undefined,
          }),
        ) as DecideResult,
      'Could not save these decisions',
    ),
    onSuccess: (_data, { etrid }) => invalidate(etrid),
  });
};
